import { ClientError, type RequestError } from "./request.ts";

type BrowserWindow = {
  location: { pathname: string; replace: (url: string) => void };
};
declare const window: BrowserWindow | undefined;

/**
 * Stable signal raised by the `requireOAuthAdmin` middleware: a user with an
 * admin role authenticated via Firebase. The frontend uses this exact message
 * (status 403) to discriminate "wrong auth method" from generic 403s so it
 * can force a re-auth through Google OAuth.
 */
export const FIREBASE_DISALLOWED_FOR_ADMIN_MESSAGE =
  "Firebase authentication is disabled for admin accounts — use Google OAuth";

/**
 * Stable signal raised by the auth middleware: the Firebase token is valid but
 * no account row matches its uid (an account deleted under an open session, or
 * a local database reseeded without it). Unlike a stale token, retrying cannot
 * help, so even a `keep-session` app signs out on it.
 */
export const NO_ACCOUNT_FOR_FIREBASE_USER_MESSAGE =
  "No account exists for this Firebase user";

export type AuthErrorConfig = {
  logout: () => void | Promise<void>;
  /**
   * What a 401 does. `end-session` logs out and redirects to the login path.
   * `keep-session` leaves the session alone: the request fails and its caller
   * shows the error. The webapp keeps the session because a 401 there is
   * usually a stale token on a request that outlived a suspended webview, not
   * a revoked account. Firebase signs it out when a token refresh proves the
   * credential invalid (`useFirebaseUser`). A 401 that names no account
   * (`NO_ACCOUNT_FOR_FIREBASE_USER_MESSAGE`) ends the session in both modes.
   */
  onUnauthorized: "end-session" | "keep-session";
  memorizeRedirectPath?: () => void;
  getLoginPath?: () => string;
  /**
   * Login path that auto-initiates Google OAuth. Used when the backend forces
   * re-auth via OAuth (403 from `requireOAuthAdmin`). Defaults to
   * `getLoginPath` — override when the regular login is a Firebase form and a
   * separate route owns the OAuth bootstrap.
   */
  getOAuthLoginPath?: () => string;
  shouldSkipPath?: (path: string) => boolean;
  /** Browser paths that should not trigger session expired redirect (e.g. /welcome, /signup) */
  publicBrowserPaths?: string[];
  beforeRedirect?: (pathname: string) => void;
  showToast?: (message: string) => void;
  sessionExpiredMessage?: string;
  forcedOAuthMessage?: string;
};

export type AuthErrorHandler = {
  /** onRequestError handler — logs out and redirects to login on session-expiry 401s. */
  handle: (error: RequestError) => void;
  /**
   * True iff `error` is a 401 the handler considers an expected end of session.
   * Sentry/observability layers should suppress capture for these — they're a
   * normal session lifecycle event, not a bug. A 401 the handler does NOT
   * consider a session expiry (e.g. one on a public path) returns false here,
   * so callers can still capture it.
   *
   * Broader than "would redirect" by one case: a 401 raised while the user is
   * already ON the login screen is expected too — more so, not less — even
   * though there is nowhere left to redirect it to.
   *
   * Note: `isRedirecting` is a runtime double-fire guard, NOT a semantic
   * "this is unexpected" signal — it's intentionally excluded so subsequent
   * 401s during the redirect chain are still classified as session-expiry.
   */
  isHandledSessionExpiry: (error: RequestError) => boolean;
  /**
   * True iff `error` is the `requireOAuthAdmin` 403 signalling that the
   * caller authenticated via Firebase but has an admin role. The handler
   * clears tokens and redirects to the OAuth login path; Sentry should
   * suppress capture — this is a normal "switch auth method" outcome, not a
   * bug.
   */
  isHandledForcedOAuth: (error: RequestError) => boolean;
};

/**
 * The login path may carry a query (admin parks a signed-out user on
 * "/?signed-out"), and `window.location.pathname` never does — comparing the
 * two raw strings makes the "already on the login screen" guards below always
 * false, so a 401 raised BY the login screen bounces it through a full page
 * reload instead of being ignored.
 */
function pathnameOf(path: string): string {
  const queryIndex = path.search(/[?#]/);
  return queryIndex === -1 ? path : path.slice(0, queryIndex);
}

/** Browser-only: Creates an auth error handler that logs out and redirects to login */
export function createAuthErrorHandler(
  config: AuthErrorConfig,
): AuthErrorHandler {
  const {
    logout,
    onUnauthorized,
    memorizeRedirectPath,
    getLoginPath = () => "/",
    getOAuthLoginPath = getLoginPath,
    shouldSkipPath,
    publicBrowserPaths,
    beforeRedirect,
    showToast,
    sessionExpiredMessage = "Session expired",
    forcedOAuthMessage = "Please sign in with Google to continue",
  } = config;
  let isRedirecting = false;

  /**
   * A 401 that means "this session is over", whatever the app does next. Kept
   * apart from `wouldRedirect` on purpose: being ALREADY on the login screen
   * removes the redirect, not the expectedness. Folding the two together sent
   * every expired token that landed on a login screen to Sentry at
   * level:error, which is the one place an expired token is least surprising.
   */
  function isSessionExpiry(error: RequestError): boolean {
    if (window === undefined) return false; // SSR guard
    if (!(error instanceof ClientError)) return false;
    if (error.responseStatus !== 401) return false;
    if (shouldSkipPath?.(error.path) === true) return false;
    if (
      publicBrowserPaths?.some((p) => window.location.pathname.startsWith(p)) ??
      false
    )
      return false;
    return true;
  }

  function wouldRedirect(error: RequestError): boolean {
    if (window === undefined) return false; // SSR guard
    if (!isSessionExpiry(error)) return false;
    if (
      onUnauthorized === "keep-session" &&
      error.message !== NO_ACCOUNT_FOR_FIREBASE_USER_MESSAGE
    )
      return false;
    if (window.location.pathname === pathnameOf(getLoginPath())) return false;
    return true;
  }

  function wouldForceOAuth(error: RequestError): boolean {
    if (window === undefined) return false; // SSR guard
    if (!(error instanceof ClientError)) return false;
    if (error.responseStatus !== 403) return false;
    if (error.message !== FIREBASE_DISALLOWED_FOR_ADMIN_MESSAGE) return false;
    if (window.location.pathname === pathnameOf(getOAuthLoginPath()))
      return false;
    return true;
  }

  function performRedirect(
    reason: string,
    targetPath: string,
    toastMessage: string,
  ): void {
    if (isRedirecting) return;
    if (window === undefined) return;

    isRedirecting = true;
    // eslint-disable-next-line no-console
    console.warn(`[api] ${reason}, redirecting to ${targetPath}`);

    beforeRedirect?.(window.location.pathname);
    memorizeRedirectPath?.();
    showToast?.(toastMessage);

    Promise.resolve(logout()).catch(() => {});
    window.location.replace(targetPath);
  }

  function handle(error: RequestError): void {
    if (wouldRedirect(error)) {
      performRedirect(
        `401 Unauthorized on ${error.path}`,
        getLoginPath(),
        sessionExpiredMessage,
      );
      return;
    }
    if (wouldForceOAuth(error)) {
      performRedirect(
        `403 OAuth required on ${error.path}`,
        getOAuthLoginPath(),
        forcedOAuthMessage,
      );
      return;
    }
  }

  return {
    handle,
    isHandledSessionExpiry: isSessionExpiry,
    isHandledForcedOAuth: wouldForceOAuth,
  };
}
